import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

// As AG's gridSizeChanged refit on a performance board, which waits for the
// resize to settle
const RESIZE_DEBOUNCE_MS = 250;

// The width inside an element (without its scrollbar), followed as it resizes
// once each resize settles. While paused, the width holds (a slide-out laid
// over the table, where AG doesn't refit), catching up when unpaused.
const useContainerWidth = ({
  ref,
  isEnabled,
  isPaused,
}: {
  ref: RefObject<HTMLElement>;
  isEnabled: boolean;
  isPaused: boolean;
}) => {
  const [width, setWidth] = useState<number>(0);
  const isPausedRef = useRef<boolean>(isPaused);
  isPausedRef.current = isPaused;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!isEnabled || !element) {
      return;
    }
    if (!isPausedRef.current) {
      setWidth(element.clientWidth);
    }
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (!isPausedRef.current) {
          setWidth(element.clientWidth);
        }
      }, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isEnabled, ref]);

  // Unpaused: catch up with whatever resize happened meanwhile
  useEffect(() => {
    const element = ref.current;
    if (isEnabled && !isPaused && element) {
      setWidth(element.clientWidth);
    }
  }, [isEnabled, isPaused, ref]);

  return width;
};

export default useContainerWidth;
